import React, { useState } from 'react';

import { useForm } from 'react-hook-form';

import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import {
  Modal,
  useTranslation,
  toastify,
  handleFieldUpdate,
  FIELD_NAMES,
  useInputTextValidationSchema,
} from '@testsigma/atoms';

import { AgentService } from '@services/agent.service';

import { TOASTIFY_OPTIONS, DEFAULT_VALUES } from './constants';
import ModalContent from './components/modal-content';
import { validateFiles } from './utils';

interface ReportAgentModalProps {
  isOpen: boolean;
  onClose: () => void;
  agentIds: string[];
  showFile?: boolean;
}
interface ReportAgentModalFormProps {
  description: string;
  fileName?: string;
  file?: File;
}

const ReportAgentModal: React.FC<ReportAgentModalProps> = ({
  isOpen,
  onClose,
  agentIds = [],
  showFile,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const descriptionDefaultSchema = useInputTextValidationSchema(
    FIELD_NAMES.DESCRIPTION,
    {
      hasDescription: true,
    },
  );
  const schema = yup.object().shape({
    description: descriptionDefaultSchema, // Use the custom hook for validation
    file: yup.mixed(),
    fileName: yup.string(),
  });

  const { setValue, ...restFormValues } = useForm<ReportAgentModalFormProps>({
    resolver: yupResolver(schema),
    defaultValues: DEFAULT_VALUES,
  });

  const onChangeDescription = (value: string) =>
    handleFieldUpdate({ setValue, fieldName: 'description', value });

  const onSubmit = ({ description, file }) => {
    if (showFile && !file) {
      toastify(t('agent.no_file_warning'), {
        ...TOASTIFY_OPTIONS,
        type: 'warning',
      });
      return;
    }
    setLoading(true);
    const formData = new FormData();
    if (file) formData.append('file', file);
    formData.append('description', description);
    agentIds.forEach((id) => formData.append('agentIds', id));
    AgentService.reportAgent(formData)
      .then(() => {
        toastify(t('agent.agent_report_success'), {
          ...TOASTIFY_OPTIONS,
          type: 'success',
        });
      })
      .catch(() => {
        toastify(t('agent.agent_report_failure'), {
          ...TOASTIFY_OPTIONS,
          type: 'error',
        });
      })
      .finally(() => {
        setLoading(false);
        onClose();
      });
  };
  const onFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!validateFiles(Array.from(e.target.files))) {
      toastify('File type is not supported', {
        type: 'error',
      });
      return;
    }
    const file = e.target.files[0];
    if (file) {
      setValue('file', file);
      setValue('fileName', file.name);
    }
  };
  const onFileRemove = () => {
    handleFieldUpdate({ setValue, fieldName: 'file', value: undefined });
    handleFieldUpdate({ setValue, fieldName: 'fileName', value: '' });
  };

  const renderModalContent = () => (
    <ModalContent
      {...restFormValues}
      onClose={onClose}
      loading={loading}
      onSubmit={onSubmit}
      onChangeDescription={onChangeDescription}
      showFile={showFile}
      onFileSelect={onFileSelect}
      onFileRemove={onFileRemove}
    />
  );

  return (
    <Modal
      className='flex items-start rounded-xl w-[28.125rem] min-h-[450px]'
      isOpen={isOpen}
      onClose={onClose}
      closeOnEscape
      closeOnOutsideClick
    >
      {renderModalContent()}
    </Modal>
  );
};

export default ReportAgentModal;
